import Immutable from 'seamless-immutable';
import { assetRefService } from '../../common/Services';

import RefData from '../../ref-data';

/**
 * Theme
 * @typedef {Object} Theme
 * @property {number} id - the theme id
 * @property {string} lastUpdated - datetime audit string for last time record was updated
 * @property {Object} modifiedBy - id and name of last person to update the record
 * @property {string} name - the theme name
 * @property {string} thumbnail - the theme thumbnail assetRef
 * @property {string} editorConfig - json string of the current theme designer editor configuration
 * @property {string} assetsConfig - json string representing the default fonts, arrow icons and image assets available to use in the theme
 * @property {string} baseTheme - the base system theme on which this theme is based
 * @property {string} compilePath - the compile path for the output theme
 * @property {string} configVariables - the config for the editors in theme designer
 * @property {string} configurable - is the theme configurable in the theme designer advanced mode?
 * @property {string} containerRef - the theme container reference
 * @property {string} moduleStyles - the value of the module styles in the theme designer
 * @property {string} overrides - the value of the CSS overrides in the theme designer
 * @property {string} typeRef - the theme type
 * @property {string} validForFormatType - what experience types is the theme compatible with
 * @property {string} viewport - the value of the viewport field in the theme designer
 */

/**
 * Create a ThemeModel from provided data
 */
function create(theme = {}) {
  const result = _.clone(theme);

  if (theme.typeRef) {
    result.typeRef = RefData.Model.hydrate(theme.typeRef);
  }

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend theme object into a frontend ThemeModel
 * @param {Object} theme - backend formatted theme
 * @returns {Theme}
 */
function deserialize(theme) {
  const result = _.pick(theme, _keys);

  return create(result);
}

/**
 * Transform a ThemeModel into a backend theme object
 * @returns {Object}
 */
function serialize(theme) {
  const result = theme.asMutable({ deep: true });

  result.typeRef = theme.typeRef.id;

  return _.pick(result, _keys);
}

/** The ThemeModel prototype */
const model = {
  get thumbnailPath() {
    return assetRefService.pathFromAssetRef(
      this.thumbnail || assetRefService.buildAssetRef('static', 'image', 'auto', 'c/img/icons/theme/theme-default.svg')
    );
  },
  get isLibrary() {
    return this.containerRef.indexOf('library') > -1;
  },
  get isConfigurable() {
    return this.configurable;
  },
};

/**
 * An array of keys on the API Theme object that should be shared between the API requests and responses
 */
const _keys = [
  'assetsConfig',
  'baseTheme',
  'compilePath',
  'configVariables',
  'configurable',
  'containerRef',
  'cssurl',
  'editorConfig',
  'id',
  'lastUpdated',
  'modifiedBy',
  'moduleStyles',
  'name',
  'overrides',
  'status',
  'thumbnail',
  'typeRef',
  'validForFormatType',
  'viewport',
  'parentId',
  'clone',
];

export default {
  create,
  deserialize,
  serialize,
};
